import { PREVIEW_INSPECTOR_COLOR } from '@/components/preview/inspector-style';
import { createPreviewBridgeLifecycle } from '@/components/github/preview-bridge-lifecycle';
import type { CanvasElementSelection } from '@/lib/canvas/shared';
import type {
  PreviewAnnotationCancelRequest,
  PreviewAnnotationCaptureRequest,
  PreviewAnnotationCaptureResult,
  PreviewAnnotationRenderRequest,
} from '@/components/preview/annotation-bridge';

export type CanvasPreviewMessageToFrame =
  | PreviewAnnotationCaptureRequest
  | PreviewAnnotationCancelRequest
  | { source: 'attune-host'; type: 'selection-mode'; enabled: boolean }
  | { source: 'attune-host'; type: 'clear-selection' };

export type CanvasPreviewMessageFromFrame =
  | PreviewAnnotationCaptureResult
  | { source: 'attune-canvas'; type: 'ready' }
  | { source: 'attune-canvas'; type: 'preview-updating' }
  | { source: 'attune-canvas'; type: 'inspector-state'; enabled: boolean }
  | { source: 'attune-canvas'; type: 'selection'; selection: CanvasElementSelection };

export function connectCanvasPreviewBridge({
  iframe,
  hostWindow,
  live,
  selectionMode,
  onMessage,
  onReadyChange,
  onUnavailable,
  onFrameReset,
}: {
  iframe: HTMLIFrameElement;
  hostWindow: Window;
  live: boolean;
  selectionMode: () => boolean;
  onMessage: (message: CanvasPreviewMessageFromFrame) => void;
  onReadyChange: (ready: boolean) => void;
  onUnavailable: () => void;
  onFrameReset?: () => void;
}) {
  // Compiled previews have an opaque origin because their sandbox omits allow-same-origin.
  const origin = live ? new URL(iframe.src, hostWindow.location.href).origin : 'null';
  let active = true;
  let capture: {
    id: string;
    rendererSource: string;
    controller: AbortController;
    rendering: boolean;
  } | null = null;
  const stopCapture = () => {
    capture?.controller.abort();
    capture = null;
  };
  const send = (message: CanvasPreviewMessageToFrame) => {
    if (!active) return;
    if (!live && message.type === 'annotation-cancel') {
      if (message.requestId !== capture?.id) return;
      stopCapture();
    }
    if (!live && message.type === 'annotation-capture') {
      // Release source work in every phase before posting the replacement.
      if (capture)
        iframe.contentWindow?.postMessage(
          {
            source: 'attune-host',
            type: 'annotation-cancel',
            requestId: capture.id,
          },
          '*'
        );
      stopCapture();
      capture = {
        id: message.requestId,
        rendererSource: message.rendererSource || '',
        controller: new AbortController(),
        rendering: false,
      };
      // Fetch only the host-configured bundle URL, never a resource requested by
      // the opaque frame. Ordinary preview loading does not inspect image bytes.
      if (iframe.src && !iframe.srcdoc) {
        const current = capture;
        const metadataUrl = new URL(iframe.src, hostWindow.location.href);
        metadataUrl.searchParams.set('capture-metadata', '1');
        void hostWindow
          .fetch(metadataUrl, { signal: current.controller.signal, credentials: 'omit' })
          .then(async response => {
            if (!response.ok) throw new Error('Preview image inspection failed. Try again.');
            const metadata = await response.json();
            if (active && capture === current)
              iframe.contentWindow?.postMessage(
                { ...message, staticImageUrls: metadata.staticImageUrls },
                '*'
              );
          })
          .catch(error => {
            if (!active || capture !== current) return;
            stopCapture();
            onMessage({
              source: 'attune-preview-annotation',
              type: 'annotation-result',
              requestId: current.id,
              error: error instanceof Error ? error.message : 'Preview image inspection failed.',
            });
          });
        return;
      }
    }
    iframe.contentWindow?.postMessage(message, live ? origin : '*');
  };
  const handshake = () =>
    send({ source: 'attune-host', type: 'selection-mode', enabled: selectionMode() });
  const lifecycle = live
    ? createPreviewBridgeLifecycle({ install: handshake, onUnavailable })
    : null;
  const receive = (event: MessageEvent) => {
    if (!active || event.source !== iframe.contentWindow || event.origin !== origin) return;
    const message = event.data as CanvasPreviewMessageFromFrame | PreviewAnnotationRenderRequest;
    if (message?.source === 'attune-preview-annotation') {
      if (!live && message.type === 'annotation-render') {
        const current = capture;
        if (
          !current ||
          current.rendering ||
          message.requestId !== current.id ||
          !current.rendererSource
        )
          return;
        current.rendering = true;
        void import('@/components/preview/isolated-capture')
          .then(({ renderIsolatedPreview }) => {
            if (current.controller.signal.aborted) throw new Error('Capture cancelled.');
            return renderIsolatedPreview(
              hostWindow,
              message,
              current.rendererSource,
              current.controller.signal
            );
          })
          .then(snapshot => {
            if (active && capture === current)
              iframe.contentWindow?.postMessage(
                {
                  source: 'attune-host',
                  type: 'annotation-render-result',
                  requestId: current.id,
                  snapshot,
                },
                '*'
              );
          })
          .catch(error => {
            if (active && capture === current)
              iframe.contentWindow?.postMessage(
                {
                  source: 'attune-host',
                  type: 'annotation-render-result',
                  requestId: current.id,
                  error: error instanceof Error ? error.message : 'Preview capture failed.',
                },
                '*'
              );
          });
        return;
      }
      if (
        message.type !== 'annotation-result' ||
        typeof message.requestId !== 'string' ||
        !message.requestId ||
        message.requestId.length > 160
      )
        return;
      if (message.requestId === capture?.id) stopCapture();
      onMessage(message);
      return;
    }
    if (message?.source !== 'attune-canvas') return;
    switch (message.type) {
      case 'ready':
        lifecycle?.acknowledged();
        onReadyChange(true);
        handshake();
        break;
      case 'inspector-state':
        if (typeof message.enabled !== 'boolean') return;
        // Existing templates acknowledge selection-mode even if their initial ready was missed.
        lifecycle?.acknowledged();
        onReadyChange(true);
        break;
      case 'preview-updating':
        lifecycle?.disconnected();
        onReadyChange(false);
        break;
      case 'selection':
        if (!message.selection || typeof message.selection.nodeId !== 'string') return;
        break;
      default:
        return;
    }
    onMessage(message);
  };
  const loaded = () => {
    stopCapture();
    onFrameReset?.();
    onReadyChange(false);
    // Native ready may precede load. Always request a fresh acknowledgement after navigation.
    lifecycle?.connected();
    handshake();
  };
  hostWindow.addEventListener('message', receive);
  iframe.addEventListener('load', loaded);
  onReadyChange(false);
  lifecycle?.connected();
  return {
    send,
    dispose() {
      active = false;
      stopCapture();
      lifecycle?.dispose();
      hostWindow.removeEventListener('message', receive);
      iframe.removeEventListener('load', loaded);
    },
  };
}

interface CanvasPreviewProtocol {
  addListener: (
    type: CanvasPreviewMessageToFrame['type'],
    listener: (message: CanvasPreviewMessageToFrame) => void
  ) => void;
  sendMessage: (message: CanvasPreviewMessageFromFrame) => void;
}

export function installAttuneCanvasPreviewBridge({
  previewWindow,
  previewProtocol,
  scope,
}: {
  previewWindow: Window;
  previewProtocol: CanvasPreviewProtocol;
  scope: { inspectorEnabled: boolean; selectionColor: string };
}) {
  const bridgeWindow = previewWindow as Window & {
    __attuneCanvasPreviewBridgeCleanup?: () => void;
  };
  bridgeWindow.__attuneCanvasPreviewBridgeCleanup?.();

  const document = previewWindow.document;
  const selectionColor = scope.selectionColor;
  let active = true;
  let selecting = Boolean(scope.inspectorEnabled);
  let hoveredElement: Element | null = null;
  let selectedElement: Element | null = null;

  const cleanText = (value: unknown, maxLength: number) =>
    typeof value === 'string'
      ? value
          .replace(/[\u0000-\u001f\u007f]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, maxLength)
      : '';
  const selectorFor = (element: Element) => {
    const id = cleanText(element.getAttribute('id'), 80);
    if (id) return `#${previewWindow.CSS.escape(id)}`;
    const parts: string[] = [];
    let current: Element | null = element;
    while (current && current !== document.documentElement && parts.length < 5) {
      const tag = current.tagName.toLowerCase();
      const parent: Element | null = current.parentElement;
      if (!parent) {
        parts.unshift(tag);
        break;
      }
      const siblings = [...parent.children].filter(child => child.tagName === current!.tagName);
      const position = siblings.indexOf(current) + 1;
      parts.unshift(siblings.length > 1 ? `${tag}:nth-of-type(${position})` : tag);
      current = parent;
    }
    return parts.join(' > ').slice(0, 180) || element.tagName.toLowerCase();
  };
  const targetFor = (event: Event) => {
    const target = event.target;
    if (!target || (target as Node).nodeType !== 1) return null;
    const element = target as Element;
    return element.hasAttribute('data-attune-preview-overlay') ? null : element;
  };
  const nodeIdFor = (element: Element) =>
    cleanText(element.closest('[data-attune-node-id]')?.getAttribute('data-attune-node-id'), 200) ||
    `dom:${selectorFor(element)}`;
  const semanticLabel = (element: Element) =>
    cleanText(
      element.getAttribute('aria-label') ||
        element.getAttribute('alt') ||
        element.getAttribute('title') ||
        element.querySelector('h1,h2,h3')?.textContent ||
        element.textContent ||
        element.tagName.toLowerCase(),
      160
    );

  const style = document.createElement('style');
  style.dataset.attuneCanvasInspector = 'true';
  style.textContent = `
    :root[data-attune-canvas-inspecting="true"],
    :root[data-attune-canvas-inspecting="true"] * { cursor: crosshair !important; }
    [data-attune-preview-overlay] {
      position: fixed;
      display: none;
      box-sizing: border-box;
      pointer-events: none;
      z-index: 2147483647;
    }
    [data-attune-preview-overlay="hover"] {
      border: 2px dashed ${selectionColor};
      background: color-mix(in srgb, ${selectionColor} 9%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, ${selectionColor} 35%, transparent);
    }
    [data-attune-preview-overlay="selected"] {
      border: 3px solid ${selectionColor};
      background: color-mix(in srgb, ${selectionColor} 6%, transparent);
      animation: attune-canvas-selection-pulse 1.6s ease-in-out infinite;
    }
    @keyframes attune-canvas-selection-pulse {
      0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, ${selectionColor} 55%, transparent); }
      50% { box-shadow: 0 0 0 5px transparent; }
    }
    @media (prefers-reduced-motion: reduce) {
      [data-attune-preview-overlay="selected"] { animation: none; }
    }
  `;
  const hoverOverlay = document.createElement('div');
  hoverOverlay.setAttribute('data-attune-preview-overlay', 'hover');
  hoverOverlay.setAttribute('aria-hidden', 'true');
  const selectedOverlay = document.createElement('div');
  selectedOverlay.setAttribute('data-attune-preview-overlay', 'selected');
  selectedOverlay.setAttribute('aria-hidden', 'true');
  document.documentElement.append(style, hoverOverlay, selectedOverlay);

  const positionOverlay = (overlay: HTMLElement, element: Element | null) => {
    if (!element?.isConnected) {
      overlay.style.display = 'none';
      return;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      overlay.style.display = 'none';
      return;
    }
    overlay.style.display = 'block';
    overlay.style.left = `${rect.left}px`;
    overlay.style.top = `${rect.top}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
    overlay.style.borderRadius = previewWindow.getComputedStyle(element).borderRadius;
  };
  const refreshOverlays = () => {
    positionOverlay(hoverOverlay, selecting ? hoveredElement : null);
    positionOverlay(selectedOverlay, selectedElement);
  };
  const clearHover = () => {
    hoveredElement = null;
    positionOverlay(hoverOverlay, null);
  };
  const clearSelection = () => {
    selectedElement = null;
    positionOverlay(selectedOverlay, null);
  };
  const announcePreviewUpdate = () => {
    if (active) {
      previewProtocol.sendMessage({ source: 'attune-canvas', type: 'preview-updating' });
    }
  };
  const setSelecting = (enabled: boolean) => {
    selecting = enabled;
    document.documentElement.toggleAttribute('data-attune-canvas-inspecting', enabled);
    if (!enabled) clearHover();
    previewProtocol.sendMessage({ source: 'attune-canvas', type: 'inspector-state', enabled });
  };
  const onPointerOver = (event: Event) => {
    if (!selecting) return;
    const target = targetFor(event);
    if (!target || target === hoveredElement) return;
    hoveredElement = target;
    positionOverlay(hoverOverlay, target);
  };
  const onPointerOut = (event: Event) => {
    if (!selecting || targetFor(event) !== hoveredElement) return;
    clearHover();
  };
  const onClick = (event: Event) => {
    if (!selecting) return;
    const target = targetFor(event);
    if (!target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    selectedElement = target;
    positionOverlay(selectedOverlay, target);
    const rect = target.getBoundingClientRect();
    const selection: CanvasElementSelection = {
      nodeId: nodeIdFor(target),
      tag: target.tagName.toLowerCase(),
      label: semanticLabel(target),
      text: cleanText(target.textContent, 500),
      bounds: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
    };
    setSelecting(false);
    previewProtocol.sendMessage({ source: 'attune-canvas', type: 'selection', selection });
  };

  previewProtocol.addListener('selection-mode', message => {
    if (active && message.type === 'selection-mode') setSelecting(message.enabled === true);
  });
  previewProtocol.addListener('clear-selection', () => {
    if (active) clearSelection();
  });
  document.addEventListener('pointerover', onPointerOver, true);
  document.addEventListener('pointerout', onPointerOut, true);
  document.addEventListener('click', onClick, true);
  previewWindow.addEventListener('scroll', refreshOverlays, true);
  previewWindow.addEventListener('resize', refreshOverlays);
  previewWindow.addEventListener('beforeunload', announcePreviewUpdate);
  const observer = new previewWindow.MutationObserver(refreshOverlays);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  bridgeWindow.__attuneCanvasPreviewBridgeCleanup = () => {
    active = false;
    observer.disconnect();
    document.documentElement.removeAttribute('data-attune-canvas-inspecting');
    style.remove();
    hoverOverlay.remove();
    selectedOverlay.remove();
    document.removeEventListener('pointerover', onPointerOver, true);
    document.removeEventListener('pointerout', onPointerOut, true);
    document.removeEventListener('click', onClick, true);
    previewWindow.removeEventListener('scroll', refreshOverlays, true);
    previewWindow.removeEventListener('resize', refreshOverlays);
    previewWindow.removeEventListener('beforeunload', announcePreviewUpdate);
  };
  setSelecting(selecting);
  previewProtocol.sendMessage({ source: 'attune-canvas', type: 'ready' });
}

export function compiledCanvasPreviewBridgeScript(): string {
  const installer = installAttuneCanvasPreviewBridge.toString();
  const scope = JSON.stringify({
    inspectorEnabled: false,
    selectionColor: PREVIEW_INSPECTOR_COLOR,
  });
  return `(() => {
    const listeners = new Map();
    window.addEventListener('message', event => {
      if (event.source !== window.parent || event.data?.source !== 'attune-host') return;
      for (const listener of listeners.get(event.data.type) || []) listener(event.data);
    });
    const previewProtocol = {
      addListener(type, listener) {
        const current = listeners.get(type) || [];
        current.push(listener);
        listeners.set(type, current);
      },
      sendMessage(message) { window.parent.postMessage(message, '*'); },
    };
    (${installer})({ previewWindow: window, previewProtocol, scope: ${scope} });
  })();`;
}
